/**
 * The drawer's save flow. Saving keeps the editor OPEN: an existing
 * container re-seeds from the server (rows created by the save adopt
 * their real ids, so the draft compares clean again); a create hands the
 * new id to the page, which swaps ?container=new for it. True once saved
 * (a master's push saves first, then pushes).
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';

import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import type { useContainerDetail } from '../../useDropGenerator';
import { useMasterMutations } from '../../hooks/useMasterMutations';
import { useAutoSendMutations } from '../board-header/autosend/use-autosend-mutations';
import { useDropGeneratorMutations } from '../../useDropGeneratorMutations';
import { apiToDraft } from './state/container-draft';
import { saveContainer } from './state/save-container';
import type { ContainerEditor } from './state/useContainerEditor';

interface Options {
  planId: string;
  countryKey: string;
  containerId: string | null;
  editor: ContainerEditor;
  detail: ReturnType<typeof useContainerDetail>;
  onSaved: (id: string) => void;
}

export function useContainerSave(options: Options) {
  const { planId, containerId, editor, detail, onSaved } = options;
  const mutations = useDropGeneratorMutations();
  const masters = useMasterMutations();
  const { update: autoSend } = useAutoSendMutations(options.countryKey);
  const { success, error } = useToast();
  const [saving, setSaving] = useState(false);

  const save = async (): Promise<boolean> => {
    setSaving(true);
    try {
      const id = await saveContainer({
        mutations,
        masters,
        draft: editor.draft,
        planId,
        containerId,
        putAutoSend: (id, patch) => autoSend.mutateAsync({ id, patch }),
        persisted: detail.data
          ? {
              enabled: detail.data.enabled,
              masterId: detail.data.masterId ?? null,
              ownSections: detail.data.ownSections ?? {},
            }
          : null,
      });
      try {
        await mutations.syncScheduledDay.mutateAsync({ countryKey: options.countryKey, containerId: id, kind: 'save' });
      } catch (err) {
        error('Saved, but today’s drafts could not be updated', `${getApiErrorMessage(err)} Use Generate to retry the sync.`);
      }
      if (containerId) {
        const fresh = await detail.refetch();
        if (fresh.data) editor.reset(apiToDraft(fresh.data));
      } else {
        onSaved(id);
      }
      success(
        editor.draft.kind === 'MASTER' ? 'Master config saved' : 'Container saved',
        editor.draft.kind === 'MASTER' ? 'Every container that follows it uses it now — today’s unsent drafts too.' : 'The board reflects it now — today’s unsent drafts too.',
      );
      return true;
    } catch (err) {
      error('Could not save the container', getApiErrorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { saving, save };
}
